import { Directory, Filesystem } from "@capacitor/filesystem";
import { FileOpener } from "@capawesome-team/capacitor-file-opener";
import { Cubit } from "blac";
import { Blac } from "blac-next";
import { nanoid } from "nanoid";
import appErrors from "src/constants/appErrors";
import type FileType from "src/constants/fileType";
import { globalEvents } from "src/constants/globalEvents";
import { DateFormats, dateLocal } from "src/lib/date";
import envVariables from "src/lib/envVariables";
import { createUnifiedFileCache } from "src/lib/fileCache";
import { fileTransferConfig } from "src/lib/fileTransferConfig";
import imageLoadPromise from "src/lib/imageLoadPromise";
import mimeType from "src/lib/mimeType";
import {
  isFlutterInAppWebView,
  sendFileToFlutterApp
} from "src/lib/flutterFileBridge";
import { isHybridApp, isIOS } from "src/lib/platform";
import reportErrorSentry from "src/lib/reportErrorSentry";
import { authenticationState, fileState, toast } from "src/state/state";
import { StorageController } from "src/state/StorageBloc/StorageBloc";
import BlockingLoadingOverlayController from "src/ui/components/BlockingLoadingOverlay/BlockingLoadingOverlayController";
import PDFViewerBloc from "src/ui/components/PDFViewer/PDFViewerBloc";

import {
  FileControllerService,
  UserShortLivedTokenControllerService
} from "@9amhealth/openapi";
import {
  blobToBase64,
  downloadFileWithProgress,
  fetchFileHeaders,
  formatBytes,
  parseFileHeaders,
  sanitizeFileName,
  triggerBlobDownload,
  uploadFileWithProgress,
  type FileHeaderMetadata
} from "@9amhealth/shared";
import { ActionSheetController } from "molecule/ActionSheet";
import translate from "src/lib/translate";

const PDFViewerController = Blac.getBloc(PDFViewerBloc);

const fileCache = createUnifiedFileCache({
  onError: (error, { operation }) => {
    reportErrorSentry(error, { context: `fileCache.${operation}` });
  }
});

interface FileAttributes {
  "file.name"?: string;
  "file.%s.name"?: string;
  "image.width"?: number;
  "image.height"?: number;
  "subscription.identity.face"?: boolean;
  "subscription.identity.id"?: boolean;
  "labs.user_result"?: boolean;
  "file.tags"?: string[];
  "file.lastModified"?: string;
  "file.size"?: number;
  "file.type"?: FileType;
  "source.name"?: string;
}

export interface FileItem {
  id: string;
  attributes: FileAttributes;
  type: string;
  blob?: Blob;
  path?: string;
  size?: number;
  error?: string;
  local?: boolean;
}

export type { FileHeaderMetadata };

export default class FilesCubit extends Cubit<FileItem[]> {
  private onAddCallbacks: Record<
    string,
    ((file: FileItem) => void) | undefined
  > = {};

  private static FILE_METADATA_CACHE_KEY = "fileMetadataCache";
  fileMetadataCache: Record<string, FileHeaderMetadata | undefined> = {};

  constructor() {
    super([]);

    this.fileMetadataCache =
      StorageController.getItemParsed<Record<string, FileHeaderMetadata>>(
        FilesCubit.FILE_METADATA_CACHE_KEY
      ) ?? {};

    window.addEventListener(globalEvents.USER_CLEAR, () => {
      this.emit([]);
      this.fileMetadataCache = {};
      void fileCache.clearFileCache();
    });
  }

  addFileMetadataCache = (
    fileId: string,
    metadata: FileHeaderMetadata
  ): void => {
    this.fileMetadataCache[fileId] = metadata;
    StorageController.setItem(
      FilesCubit.FILE_METADATA_CACHE_KEY,
      JSON.stringify(this.fileMetadataCache)
    );
  };

  public readonly getFileById = (id: string): FileItem | undefined => {
    return this.state.find((item) => item.id === id);
  };

  public readonly waitForFile = async (
    id: string,
    timeoutMs = 30000
  ): Promise<FileItem | null> => {
    return new Promise((resolve) => {
      const index = nanoid(5);

      const cleanup = (): void => {
        this.onAddCallbacks[index] = undefined;
      };

      const timeout = setTimeout(() => {
        cleanup();
        resolve(null);
      }, timeoutMs);

      this.onAddCallbacks[index] = (file): void => {
        if (file.id === id) {
          clearTimeout(timeout);
          cleanup();
          resolve(file);
        }
      };
    });
  };

  public readonly getFileByAttribute = (
    attribute: FileAttributes
  ): FileItem[] => {
    return this.state.filter((item) => {
      const attributeName = Object.keys(attribute)[0] as never;
      return (
        Object.keys(item.attributes).includes(attributeName) &&
        item.attributes[attributeName] === attribute[attributeName]
      );
    });
  };

  public readonly uploadFile = async (options: {
    file: File;
    fileAttributes?: FileAttributes;
  }): Promise<FileItem | undefined> => {
    try {
      const { file, fileAttributes } = options;

      const attributes: FileAttributes = {
        "source.name": envVariables.APP_ENV
      };

      const name = sanitizeFileName(file.name);

      attributes["file.name"] = name;
      attributes["file.lastModified"] = dateLocal(file.lastModified).format(
        DateFormats.ISO_FULL
      );
      attributes["file.size"] = file.size;
      const filePath = URL.createObjectURL(file);

      if (mimeType.isImage(file.type)) {
        const img = await imageLoadPromise(filePath);

        attributes["image.width"] = img.width;
        attributes["image.height"] = img.height;
      }

      const allAttributes = {
        ...attributes,
        ...fileAttributes
      };

      const fileAttributesHeader = JSON.stringify({
        "file.name": allAttributes["file.name"],
        "file.type": fileAttributes?.["file.type"],
        ...(allAttributes["image.width"] && {
          "image.width": String(allAttributes["image.width"])
        }),
        ...(allAttributes["image.height"] && {
          "image.height": String(allAttributes["image.height"])
        })
      });

      const response = await FileControllerService.createFile(
        file,
        fileAttributesHeader
      );

      const item: FileItem = {
        id: response.data.fileId,
        attributes: allAttributes,
        local: true,
        type: file.type,
        path: filePath
      };

      this.emit([...this.state, item]);
      return item;
    } catch (e: unknown) {
      toast.error("error_upload_file_failed");
      reportErrorSentry(e);
    }
  };

  public readonly uploadFileWithProgress = async (
    options: {
      file: File;
      fileAttributes?: FileAttributes;
    },
    showProgress = true,
    customCallbacks?: {
      onProgress?: (loaded: number, total: number, percentage: number) => void;
    }
  ): Promise<FileItem | undefined> => {
    const { file, fileAttributes } = options;
    const abortController = new AbortController();

    try {
      const attributes: FileAttributes = {
        "source.name": envVariables.APP_ENV
      };

      const name = sanitizeFileName(file.name);

      attributes["file.name"] = name;
      attributes["file.lastModified"] = dateLocal(file.lastModified).format(
        DateFormats.ISO_FULL
      );
      attributes["file.size"] = file.size;
      const filePath = URL.createObjectURL(file);

      if (mimeType.isImage(file.type)) {
        const img = await imageLoadPromise(filePath);
        attributes["image.width"] = img.width;
        attributes["image.height"] = img.height;
      }

      const allAttributes = {
        ...attributes,
        ...fileAttributes
      };

      const fileAttributesHeader = JSON.stringify({
        "file.name": allAttributes["file.name"],
        "file.type": fileAttributes?.["file.type"],
        ...(allAttributes["image.width"] && {
          "image.width": String(allAttributes["image.width"])
        }),
        ...(allAttributes["image.height"] && {
          "image.height": String(allAttributes["image.height"])
        })
      });

      if (showProgress) {
        BlockingLoadingOverlayController.startLoadingWithProgress({
          title: "upload.uploading",
          message: "upload.pleaseWait",
          bg: "transparent",
          onCancel: () => {
            abortController.abort();
            BlockingLoadingOverlayController.endLoading();
          }
        });
      }

      const response = await uploadFileWithProgress(
        file,
        fileTransferConfig,
        fileAttributesHeader,
        {
          signal: abortController.signal,
          onProgress: (loaded, total, percentage) => {
            customCallbacks?.onProgress?.(loaded, total, percentage);
            if (showProgress) {
              const label = `${formatBytes(loaded)} / ${formatBytes(total)}`;
              BlockingLoadingOverlayController.updateProgress(
                percentage,
                label
              );
            }
          }
        }
      );

      const item: FileItem = {
        id: response.fileId,
        attributes: allAttributes,
        local: true,
        type: file.type,
        path: filePath
      };

      this.emit([...this.state, item]);

      if (showProgress) {
        BlockingLoadingOverlayController.loadingSuccess({
          title: "upload.complete"
        });

        setTimeout(() => {
          BlockingLoadingOverlayController.endLoading();
        }, 1500);
      }

      return item;
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        if (showProgress) {
          BlockingLoadingOverlayController.endLoading();
        }
        return undefined;
      }

      reportErrorSentry(error);
      toast.error("error_upload_file_failed");

      if (showProgress) {
        BlockingLoadingOverlayController.loadingError({
          title: "upload.failed",
          message: "error.uploadFile",
          retry: () => {
            void this.uploadFileWithProgress(options, showProgress);
          }
        });
      }
    }
  };

  static requestDownloadFile = async (id: string): Promise<Response> =>
    fetch(`${envVariables.API_BASE_URL}/v1/files/${id}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${authenticationState.accessToken}`
      }
    });

  hasFileMetadata = (id: string): boolean => {
    return Boolean(this.fileMetadataCache[id]);
  };

  getFileMetadata = async (id: string): Promise<FileHeaderMetadata | null> => {
    if (this.fileMetadataCache[id]) {
      return this.fileMetadataCache[id];
    }

    const metadata = await fetchFileHeaders(id, fileTransferConfig);
    if (metadata) {
      this.addFileMetadataCache(id, metadata);
    }
    return metadata;
  };

  getFileContentType = async (id: string): Promise<string | null> => {
    const metadata = await this.getFileMetadata(id);
    return metadata?.contentType ?? null;
  };

  static requestDownloadFilePublicUrl = async (id: string): Promise<string> => {
    const shortLived =
      await UserShortLivedTokenControllerService.retrieveShortLivedToken();
    const { accessToken } = shortLived.data;
    return `${envVariables.API_BASE_URL}/v1/files/${id}?authToken=${accessToken}`;
  };

  static requestDownloadFilePreview = async (
    id: string,
    w: number,
    h: number
  ): Promise<Response> =>
    fetch(`${envVariables.API_BASE_URL}/v1/files/${id}/preview/${w}/${h}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${authenticationState.accessToken}`
      }
    });

  public readonly addFile = (id: string, fileBlob: Blob): FileItem => {
    const file: FileItem = {
      id,
      attributes: {},
      type: ""
    };

    try {
      file.path = URL.createObjectURL(fileBlob);
      this.emit([...this.state, file]);
      this.addNotify(file);
    } catch (e: unknown) {
      // eslint-disable-next-line no-console
      console.error(e);
      return { ...file, error: appErrors.generic };
    }

    return file;
  };

  static showFileActionSheet = async (id: string): Promise<void> => {
    const isNative = isHybridApp();
    const saveLabel = isNative
      ? translate("preview.save")
      : translate("preview.download");

    try {
      const previewLabel = isNative
        ? translate("preview.open")
        : translate("preview.preview");

      if (!fileState.hasFileMetadata(id)) {
        ActionSheetController.showLoading();
      }

      const metadata = await fileState.getFileMetadata(id);
      const isPdf = metadata?.contentType === "application/pdf";

      const result = await ActionSheetController.showActions({
        buttons: [
          { id: "preview", title: previewLabel, enabled: isPdf || isNative },
          { id: "save", title: saveLabel },
          { id: "share", title: translate("preview.share"), enabled: !isNative }
        ]
      });

      if (result === "cancel") {
        return;
      }

      // For native preview, try to open directly from cache without loading blob
      if (result === "preview" && isNative) {
        const userId = authenticationState.activeUserId;
        if (userId) {
          const cachedInfo = await fileCache.getCachedFileInfo?.(id, userId);
          if (cachedInfo?.uri) {
            const fileExists = await FilesCubit.checkFileExists(cachedInfo.uri);
            if (fileExists) {
              await FilesCubit.openFileNative(
                cachedInfo.uri,
                cachedInfo.mimeType
              );
              return;
            }
            // File doesn't exist, remove stale cache entry
            await fileCache.removeCachedFile(id, userId);
          }
        }
      }

      const downloadResult = await FilesCubit.downloadFileWithOverlay(id);
      if (!downloadResult) {
        return;
      }

      const {
        blob,
        wasCached,
        metadata: downloadMetadata,
        cachedUri
      } = downloadResult;

      // Use metadata from download if available, fallback to pre-fetched metadata
      const finalFilename =
        downloadMetadata?.filename ?? metadata?.filename ?? `file_${id}`;

      if (result === "preview") {
        await FilesCubit.openFilePreview(blob, finalFilename, cachedUri);
      } else if (result === "share") {
        await FilesCubit.shareFile(blob, finalFilename, wasCached);
      } else {
        await FilesCubit.downloadFile(blob, finalFilename);
      }
    } catch (error) {
      reportErrorSentry(error);
      ActionSheetController.hide();
      toast.error("error.downloadFile");
    }
  };

  private static downloadFileWithOverlay = async (
    id: string
  ): Promise<{
    blob: Blob;
    wasCached: boolean;
    metadata: FileHeaderMetadata | null;
    cachedUri?: string;
  } | null> => {
    const userId = authenticationState.activeUserId;

    if (userId) {
      const cached = await fileCache.getCachedFile(id, userId);
      if (cached) {
        return {
          blob: cached.blob,
          wasCached: true,
          metadata: null,
          cachedUri: cached.uri
        };
      }
    }

    const abortController = new AbortController();
    let metadata: FileHeaderMetadata | null = null;

    try {
      BlockingLoadingOverlayController.startLoadingWithProgress({
        title: "download.downloading",
        message: "download.pleaseWait",
        bg: "transparent",
        onCancel: () => {
          abortController.abort();
          BlockingLoadingOverlayController.endLoading();
        }
      });

      const blob = await downloadFileWithProgress(id, fileTransferConfig, {
        signal: abortController.signal,
        onHeaders: (headers) => {
          metadata = parseFileHeaders(headers);
        },
        onProgress: (loaded, total, percentage) => {
          const label =
            total > 0
              ? `${formatBytes(loaded)} / ${formatBytes(total)}`
              : formatBytes(loaded);

          BlockingLoadingOverlayController.updateProgress(percentage, label);
        }
      });

      BlockingLoadingOverlayController.endLoading();

      let cachedUri: string | undefined;
      if (userId) {
        const filename =
          (metadata as FileHeaderMetadata | null)?.filename ?? `file_${id}`;
        await fileCache.cacheFile(id, userId, blob, filename, blob.type);
        const cachedInfo = await fileCache.getCachedFileInfo?.(id, userId);
        cachedUri = cachedInfo?.uri;
      }

      return { blob, wasCached: false, metadata, cachedUri };
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        BlockingLoadingOverlayController.endLoading();
        return null;
      }

      reportErrorSentry(error);

      return new Promise((resolve) => {
        BlockingLoadingOverlayController.loadingError({
          title: "download.failed",
          message: "error.downloadFile",
          retry: () => {
            FilesCubit.downloadFileWithOverlay(id)
              .then(resolve)
              .catch(() => resolve(null));
          }
        });
      });
    }
  };

  static checkFileExists = async (uri: string): Promise<boolean> => {
    try {
      await Filesystem.stat({ path: uri });
      return true;
    } catch {
      return false;
    }
  };

  static openFileNative = async (
    uri: string,
    mimeType: string
  ): Promise<void> => {
    try {
      await FileOpener.openFile({
        path: uri,
        mimeType: mimeType || "application/octet-stream"
      });
    } catch (error) {
      reportErrorSentry(error);
      toast.error("error.openFileFailed");
    }
  };

  static openFilePreview = async (
    blob: Blob,
    filename: string,
    cachedUri?: string
  ): Promise<void> => {
    try {
      const isPDF =
        blob.type === "application/pdf" ||
        filename.toLowerCase().endsWith(".pdf");

      if (isPDF) {
        if (isHybridApp()) {
          await FilesCubit.openPDFNative(blob, filename, cachedUri);
        } else {
          const blobUrl = URL.createObjectURL(blob);
          PDFViewerController.open(blobUrl, filename);
        }
      } else if (isHybridApp() && cachedUri) {
        await FilesCubit.openFileNative(cachedUri, blob.type);
      } else if (!isHybridApp()) {
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, "_blank");
      }
    } catch (error) {
      reportErrorSentry(error);
      toast.error("error.openFileFailed");
    }
  };

  static downloadFile = async (blob: Blob, filename: string): Promise<void> => {
    if (isFlutterInAppWebView()) {
      let sent = false;
      try {
        sent = await sendFileToFlutterApp(blob, filename);
      } catch (error) {
        sent = false;
        reportErrorSentry(
          new Error("Error sending file to Flutter app", { cause: error })
        );
        toast.error("error.downloadFile");
      }
      if (!sent) {
        reportErrorSentry(
          new Error("Failed to send file to Flutter app - Silent")
        );
        toast.error("error.downloadFile");
      }
      return;
    }

    if (isHybridApp()) {
      await FilesCubit.downloadFileNative(blob, filename);
    } else {
      triggerBlobDownload(blob, filename);
    }
  };

  static downloadFileNative = async (
    blob: Blob,
    filename: string
  ): Promise<void> => {
    try {
      const base64Data = await blobToBase64(blob);

      const result = await Filesystem.writeFile({
        path: filename,
        data: base64Data,
        directory: Directory.Documents
      });

      const mimeType = blob.type || "application/octet-stream";

      toast.show("download.savedToLocation", {
        severity: "success",
        translationVariables: {
          location: isIOS()
            ? translate("download.location.ios")
            : translate("download.location.android")
        },
        buttons: [
          {
            text: translate("download.openFile"),
            handler: () => {
              void FileOpener.openFile({
                path: result.uri,
                mimeType
              });
            }
          }
        ]
      });
    } catch (error) {
      reportErrorSentry(error);
      toast.error("error.downloadFile");
    }
  };

  static fileNameWithExtension = (
    filename: string,
    fileType: string
  ): string => {
    return filename.includes(".")
      ? filename
      : fileType
        ? `${filename}.${fileType.split("/")[1]}`
        : filename;
  };

  static shareFile = async (
    blob: Blob,
    filename: string,
    skipOverlay = false
  ): Promise<void> => {
    const fileType = blob.type;

    const doShare = async (): Promise<void> => {
      try {
        const file = new File(
          [blob],
          FilesCubit.fileNameWithExtension(filename, fileType),
          {
            type: fileType
          }
        );
        await navigator.share({
          files: [file],
          title: filename
        });
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") {
          // User cancelled
        } else {
          reportErrorSentry(error);
          toast.error("error.shareFile");
        }
      }
    };

    if (skipOverlay) {
      await doShare();
      return;
    }

    return new Promise((resolve) => {
      BlockingLoadingOverlayController.showShareReady(
        {
          filename,
          size: blob.size,
          mimeType: fileType,
          blob
        },
        () => {
          void doShare().then(() => {
            BlockingLoadingOverlayController.endLoading();
            resolve();
          });
        }
      );
    });
  };

  public readonly removeFile = (id: string): void => {
    this.emit(this.state.filter((file) => id !== file.id));
  };

  private readonly addNotify = (file: FileItem): void => {
    for (const id in this.onAddCallbacks) {
      this.onAddCallbacks[id]?.(file);
    }
  };

  previewUrlCache: Record<string, string | undefined> = {};
  previewRequestCache: Record<string, Promise<string> | undefined> = {};
  public readonly loadPreview = async (
    id: string,
    w = 400,
    h = 400
  ): Promise<string> => {
    const requestId = `${id}-${w}-${h}`;

    const request = async (): Promise<string> => {
      try {
        const response = await FilesCubit.requestDownloadFilePreview(id, w, h);
        if (!response.ok) {
          throw new Error(`Failed to load preview for file ${id}`);
        }
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        this.previewUrlCache[requestId] = url;
        return url;
      } catch (e: unknown) {
        reportErrorSentry(e);
      }
      return "";
    };

    if (this.previewUrlCache[requestId]) {
      return this.previewUrlCache[requestId] ?? "";
    }

    if (this.previewRequestCache[requestId]) {
      return this.previewRequestCache[requestId] ?? "";
    }

    this.previewRequestCache[requestId] = request();
    return this.previewRequestCache[requestId] ?? "";
  };

  static openPDFNative = async (
    blob: Blob | undefined,
    filename: string,
    cachedUri?: string
  ): Promise<void> => {
    try {
      if (cachedUri) {
        await FileOpener.openFile({
          path: cachedUri,
          mimeType: "application/pdf"
        });
        return;
      }

      if (!blob) {
        throw new Error("No blob or cached URI provided");
      }

      const base64Data = await blobToBase64(blob);

      const pdfFilename = filename.endsWith(".pdf")
        ? filename
        : `${filename}.pdf`;

      const result = await Filesystem.writeFile({
        path: pdfFilename,
        data: base64Data,
        directory: Directory.Cache
      });

      await FileOpener.openFile({
        path: result.uri,
        mimeType: "application/pdf"
      });
    } catch (error) {
      reportErrorSentry(error);
      toast.error("error.openPdfFailed");
    }
  };
}
